'use client'

/**
 * FO-311 (batch-34) — gedeelde WeekStatusBadge met ÉÉN kleur-semantiek, app-breed
 * (automatiseringen-schema's én /planning/dekking):
 *   groen  = gepland zoals afgesproken
 *   rood   = te weinig / niet / onjuist gepland (actie nodig)
 *   oranje = afwijkend, maar geaccepteerd
 *   grijs  = geen verwachting deze week
 *
 * De kleur volgt STRIKT de afwijking (een tekort is de alarmerende kleur, niet
 * overbezetting) en de badge zegt het ook in tekst ("wk 29: 2 van 3 gepland"),
 * zodat de betekenis nooit dubbelzinnig is. Optioneel klikbaar: een popover met de
 * werkelijke planning van die week + doorklik naar dekking/planbord.
 */

import * as React from 'react'
import { Badge } from './badge'
import { Popover, PopoverContent, PopoverTrigger } from './popover'
import { cn } from '../lib/utils'

export type WeekStatusKleur = 'groen' | 'rood' | 'oranje' | 'grijs'

const KLEUR_CLASS: Record<WeekStatusKleur, string> = {
  groen: 'border-emerald-300 bg-emerald-100 text-emerald-800',
  rood: 'border-rose-300 bg-rose-100 text-rose-800',
  oranje: 'border-amber-300 bg-amber-100 text-amber-800',
  grijs: 'border-slate-300 bg-slate-100 text-slate-600',
}

const DOT_CLASS: Record<WeekStatusKleur, string> = {
  groen: 'bg-emerald-500',
  rood: 'bg-rose-500',
  oranje: 'bg-amber-500',
  grijs: 'bg-slate-400',
}

export const WEEK_STATUS_UITLEG: Record<WeekStatusKleur, string> = {
  groen: 'Gepland zoals afgesproken',
  rood: 'Te weinig / niet gepland (actie nodig)',
  oranje: 'Afwijkend, maar geaccepteerd',
  grijs: 'Geen verwachting deze week',
}

/** Onderhoudscontrole-status -> kleur (één bron met de dekking-view). */
export function onderhoudWeekKleur(
  status: 'NIET_GEPLAND' | 'ONVOLLEDIG' | 'ONJUIST' | 'OK',
  totaalVerwacht: number,
  geaccepteerd: boolean,
): WeekStatusKleur {
  if (totaalVerwacht <= 0) return 'grijs'
  if (geaccepteerd) return 'oranje'
  return status === 'OK' ? 'groen' : 'rood'
}

/** Dekking-classificatie -> kleur. Een tekort ÉN overbezetting zijn afwijkingen
 *  die aandacht vragen (rood); de tekst maakt onderscheid. Dit trekt de eerdere
 *  omgekeerde beleving recht (rood was er ten onrechte "te veel gepland").
 *  FO-348: NIET_TOEGEKEND is geen afwijking maar een koppel-actie zonder
 *  verwachting - grijs, zodat hij het rood van de echte tekorten niet verdunt. */
export function dekkingWeekKleur(
  classificatie: 'COMPLEET' | 'TE_WEINIG' | 'TE_VEEL' | 'GEACCEPTEERD' | 'NIET_TOEGEKEND',
): WeekStatusKleur {
  switch (classificatie) {
    case 'COMPLEET':
      return 'groen'
    case 'GEACCEPTEERD':
      return 'oranje'
    case 'NIET_TOEGEKEND':
      return 'grijs'
    case 'TE_WEINIG':
    case 'TE_VEEL':
      return 'rood'
  }
}

/** Korte tekst "2 van 3 gepland" / "geen verwachting" bij een kleur. */
export function weekStatusTekst(kleur: WeekStatusKleur, totaalVerwacht: number, totaalGepland: number): string {
  if (kleur === 'grijs') return 'geen verwachting'
  const basis = `${totaalGepland} van ${totaalVerwacht} gepland`
  return kleur === 'oranje' ? `${basis} (geaccepteerd)` : basis
}

export interface WeekStatusBadgeProps {
  kleur: WeekStatusKleur
  /** Tekstlabel, bv. "wk 29: 2 van 3 gepland". */
  label: string
  /** Popover-inhoud (werkelijke planning van die week). */
  popover?: React.ReactNode
  dekkingHref?: string
  planbordHref?: string
  /** Aangeroepen bij openen (lazy-load van de week-detail). */
  onOpenChange?: (open: boolean) => void
  className?: string
}

export function WeekStatusBadge({
  kleur,
  label,
  popover,
  dekkingHref,
  planbordHref,
  onOpenChange,
  className,
}: WeekStatusBadgeProps) {
  const interactief = Boolean(popover || dekkingHref || planbordHref)
  const badge = (
    <Badge
      variant="outline"
      className={cn('gap-1.5 font-medium', KLEUR_CLASS[kleur], className)}
      title={WEEK_STATUS_UITLEG[kleur]}
    >
      <span className={cn('inline-block h-2 w-2 shrink-0 rounded-full', DOT_CLASS[kleur])} aria-hidden />
      {label}
    </Badge>
  )

  if (!interactief) return badge

  return (
    <Popover onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
          aria-label={`${label} - bekijk de planning`}
        >
          {badge}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 max-w-[90vw] text-sm" align="start">
        {popover}
        {(dekkingHref || planbordHref) && (
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 border-t border-border pt-2 text-xs">
            {dekkingHref && (
              <a href={dekkingHref} className="font-medium text-primary underline underline-offset-2">
                Open in dekking
              </a>
            )}
            {planbordHref && (
              <a href={planbordHref} className="font-medium text-primary underline underline-offset-2">
                Open in planbord
              </a>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

/** Legenda naast de badges — dezelfde vier kleuren + betekenis. */
export function WeekStatusLegenda({ className }: { className?: string }) {
  const kleuren: WeekStatusKleur[] = ['groen', 'rood', 'oranje', 'grijs']
  return (
    <div className={cn('flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground', className)}>
      {kleuren.map((k) => (
        <span key={k} className="inline-flex items-center gap-1.5">
          <span className={cn('inline-block h-2.5 w-2.5 rounded-full', DOT_CLASS[k])} aria-hidden />
          {WEEK_STATUS_UITLEG[k]}
        </span>
      ))}
    </div>
  )
}
